import React, { useState, useEffect } from "react";
import {
  t,
  translate,
} from "components/CentralDataMangement/translation/Translation";
import ModalPopup from "components/common/atoms/ModalPopup";
import Button from "components/common/atoms/Button";
import SelectWithSearch from "components/common/atoms/SelectWithSearch";
import AutoCompleteDropdown from "components/common/atoms/AutoCompleteDropdown";
import CustomNotify from "components/common/atoms/CustomNotify";
import LabelWithTextAreaField from "components/common/molecules/LabelWithTextAreaField";
import Calender from "components/common/molecules/Calender";
import { ApiCall } from "components/common/services/ApiServices";
import { CREATE_CLOSURE_DETAILS } from "routes/ApiEndpoints";
import {
  validateRequired,
  validateSelectField,
  validateForm,
} from "components/common/services/ValidationService";
import { ValidationRules } from "components/common/utlis/TypeAnnotations";
import * as CONST from "../../../Constants";

interface ClosurePopupProps {
  show: boolean;
  onClose: () => void;
  onSave: () => void;
  mode: "create" | "edit";
  closureData?: any;
  closingTypesList: any[];
}

interface FormData {
  company: any;
  start_date: string;
  end_date: string;
  closingType: any;
  info: string;
}

interface FieldErrors {
  company?: string;
  start_date?: string;
  end_date?: string;
  closingType?: string;
}

const initialFormData: FormData = {
  company: null,
  start_date: "",
  end_date: "",
  closingType: null,
  info: "",
};

// Helper function to convert dd-mm-yyyy to Date object
const parseDate = (dateStr: string): Date | null => {
  if (!dateStr) return null;
  const parts = dateStr.split("-");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
  const year = parseInt(parts[2], 10);
  return new Date(year, month, day);
};

const ClosurePopup: React.FC<ClosurePopupProps> = ({
  show,
  onClose,
  onSave,
  mode,
  closureData,
  closingTypesList,
}) => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [loading, setLoading] = useState(false);

  // Pre-fill form data when modal opens
  useEffect(() => {
    if (show) {
      if (mode === "edit" && closureData) {
        const selectedClosingType = closingTypesList.find(
          (type) => type.value === closureData.closing_type_id
        );

        setFormData({
          company: {
            value: closureData.company_id,
            label: closureData.company_name,
          },
          start_date: closureData.start_date, // Keep as dd-mm-yyyy string
          end_date: closureData.end_date, // Keep as dd-mm-yyyy string
          closingType: selectedClosingType || null,
          info: closureData.info || "",
        });
      } else if (mode === "create") {
        resetForm();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, mode]);

  // Validation function
  const validation = (
    name: string,
    value: any,
    isSingleFieldValidation: boolean = false
  ) => {
    const validationRules: ValidationRules = {
      company: [validateSelectField],
      start_date: [validateRequired],
      end_date: [validateRequired],
      closingType: [validateSelectField],
    };

    const validationErrors = validateForm(
      { ...formData, [name]: value },
      validationRules,
      isSingleFieldValidation ? name : undefined
    );

    if (isSingleFieldValidation && Object.keys(fieldErrors).length > 0) {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [name]: validationErrors[name],
      }));
    } else {
      setFieldErrors(validationErrors);
    }

    return Object.keys(validationErrors).length === 0;
  };

  // Field change handlers
  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validation(name, value, true);
  };

  const handleSelectChange = (selectedOption: any, fieldName: string) => {
    setFormData((prev) => ({ ...prev, [fieldName]: selectedOption }));
    validation(fieldName, selectedOption, true);
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    // Convert Date object to dd-mm-yyyy format for storage
    const formattedDate = date
      ? `${String(date.getDate()).padStart(2, "0")}-${String(
          date.getMonth() + 1
        ).padStart(2, "0")}-${date.getFullYear()}`
      : "";
    setFormData((prev) => ({ ...prev, [fieldName]: formattedDate }));
    validation(fieldName, formattedDate, true);
  };

  // Date validation
  const validateDates = () => {
    if (formData.start_date && formData.end_date) {
      const startDate = parseDate(formData.start_date);
      const endDate = parseDate(formData.end_date);

      if (startDate && endDate && endDate < startDate) {
        setFieldErrors((prev) => ({
          ...prev,
          end_date: t("End date must be after start date"),
        }));
        return false;
      }
    }
    return true;
  };

  // Save handler
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const isValid =
      validation("company", formData.company) &&
      validation("start_date", formData.start_date) &&
      validation("end_date", formData.end_date) &&
      validation("closingType", formData.closingType);

    if (!isValid) {
      CustomNotify({
        type: "error",
        message: t("Please fill all required fields"),
      });
      return;
    }

    // Validate dates
    if (!validateDates()) {
      CustomNotify({
        type: "error",
        message: t("End date must be after start date"),
      });
      return;
    }

    setLoading(true);

    try {
      const payload: any = {
        company_id: formData.company?.value,
        start_date: formData.start_date, // Backend expects dd-mm-yyyy
        end_date: formData.end_date, // Backend expects dd-mm-yyyy
        closing_type_id: formData.closingType?.value,
        info: formData.info.trim() || null,
        bright_closing_id: closureData?.bright_closing_id || null,
      };

      // Add id for edit mode
      if (mode === "edit" && closureData?.id) {
        payload.id = closureData.id;
      }

      const response = await ApiCall.service(
        CREATE_CLOSURE_DETAILS,
        "POST",
        payload,
        false,
        CONST.COMPANY_MICROSERVICE
      );

      if (response?.status === 200) {
        CustomNotify({
          type: "success",
          message: response?.message || t("Closure saved successfully"),
        });
        resetForm();
        onSave();
        onClose();
      } else {
        CustomNotify({
          type: "error",
          message: response?.message || t("Failed to save closure"),
        });
      }
    } catch (error) {
      console.error("Error saving closure:", error);
      CustomNotify({
        type: "error",
        message: t("Some error occurred"),
      });
    } finally {
      setLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData(initialFormData);
    setFieldErrors({});
  };

  // Close handler
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <ModalPopup
      show={show}
      onHide={handleClose}
      title={mode === "create" ? t("Create closure") : t("Edit closure")}
      body={
        <div>
          <div className="row">
            <div className="col-md-6 autoCompleteFunction">
              <AutoCompleteDropdown
                label={t("Company")}
                placeholder={t("Type to search") + "..."}
                microService={CONST.COMPANY_MICROSERVICE}
                onChange={(e) => handleSelectChange(e, "company")}
                value={formData.company}
                columnName="company_name"
                modelKey="company"
                id="company"
                isMandatory={true}
                name="company"
                filter={true}
                filterInfo={[
                  {
                    column: "company_status",
                    value: 1,
                    condition: "where",
                  },
                  {
                    column: "company_status",
                    value: 0,
                    condition: "orWhere",
                  },
                ]}
                notNullColumn="bbright_id"
                error={fieldErrors.company}
                isDisabled={mode === "edit"}
              />
            </div>

            <div className="col-md-6">
              <SelectWithSearch
                title={t("Closure type")}
                name="closingType"
                isMandatory={true}
                search={true}
                options={closingTypesList}
                value={formData.closingType}
                onChange={(e) => handleSelectChange(e, "closingType")}
                isMulti={false}
                isTranslate={false}
                error={fieldErrors.closingType}
              />
            </div>

            <div className="col-md-6 position-relative">
              <Calender
                isMandatory={true}
                label={t("Start date")}
                name="start_date"
                selectedDate={parseDate(formData.start_date)}
                onChange={(date) => handleDateChange(date, "start_date")}
                error={fieldErrors.start_date}
                placeHolder="dd-mm-yyyy"
              />
            </div>

            <div className="col-md-6 position-relative">
              <Calender
                isMandatory={true}
                label={t("End date")}
                name="end_date"
                selectedDate={parseDate(formData.end_date)}
                onChange={(date) => handleDateChange(date, "end_date")}
                error={fieldErrors.end_date}
                minDate={parseDate(formData.start_date)}
                placeHolder="dd-mm-yyyy"
              />
            </div>

            <div className="col-md-12">
              <LabelWithTextAreaField
                name="info"
                label={t("Info")}
                handleChange={handleFieldChange}
                value={formData.info}
                isMandatory={false}
                placeholder={t("Additional information")}
              />
            </div>

            <div className="col-12 d-flex justify-content-end">
              <Button
                handleClick={handleClose}
                className="modal-cancel-btn shadow-none px-4 me-2"
                disabled={loading}
              >
                {t("Cancel")}
              </Button>
              <Button
                handleClick={handleSave}
                className="modal-confirm-btn shadow-none px-4"
                disabled={loading}
              >
                {loading
                  ? t("Saving...")
                  : mode === "create"
                  ? t("Create")
                  : t("Update")}
              </Button>
            </div>
          </div>
        </div>
      }
      className="modal-closure-details"
    />
  );
};

export default translate(ClosurePopup);
